import {useMemo, useState, useEffect} from 'react';
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {useLocation, useRouteMatch} from 'react-router-dom';
import {formatISO} from 'date-fns';
import {findLast, get, omit} from 'lodash';

import api from 'api';
import {
  BlogResponse,
  ListResponse,
  ManualChargeMethod,
  ManualPayerType,
  MMScriptStatus,
  Note,
  NoteSort,
  OfferingType,
  OrderType,
  RequestDetails,
  ScriptStatus,
  SelectOption,
  User,
} from 'types';
import {ScriptStatusOption} from 'components/ScriptStatusSelect';

import {getFullName} from 'helpers/user';
import useCurrentUser from 'hooks/useCurrentUser';
import {ActionMeta, SingleValue} from 'react-select';
import toast from 'react-hot-toast';
import CheckIcon from 'pages/Requests/CheckIcon';
import {OFFERING_TYPE_OPTIONS_LABELS} from 'constants/select-options';
import {useForm} from 'react-hook-form';
import useCompanyConfig from 'hooks/useCompanyConfig';

type CopayItem = {
  id: number;
  amount_due: number;
};

async function getRequestById({
  queryKey,
}: QueryFunctionContext): Promise<RequestDetails> {
  const [, id, order, status, orderType] = queryKey;
  const {data} = await api.get<RequestDetails>(
    `/requests/${id}/?is_sequential_order_number=true`,
    {
      params: {order, status, order_type: orderType},
    }
  );
  return data;
}

async function getUserById({queryKey}: QueryFunctionContext) {
  const [, userId] = queryKey;
  if (!userId) return;
  const {data} = await api.get<User>(`/users/${userId}/`);
  return data;
}

async function getMessageTemplates() {
  const {data} = await api.get<ListResponse<BlogResponse>>(
    `/support/templates/`,
    {
      params: {content_type: 'template'},
    }
  );
  return data?.results;
}

export type UpdatePreferencesParams = {
  preference_date?: string | null;
  preference_time?: string | null;
  flag_reason?: string | null;
  priority?: string;
  requestId: number;
};
async function updatePreferences(params: UpdatePreferencesParams) {
  const normalizedParams = Object.fromEntries(
    Object.entries(omit(params, 'requestId')).filter(([_, value]) => value)
  );
  const {data} = await api.patch<RequestDetails>(
    `/requests/${params.requestId}/`,
    normalizedParams
  );
  return data;
}

export type ManualCharge = {
  payment_method?: ManualChargeMethod;
  payer_type?: ManualPayerType;
  payer_name?: string;
};

function useRequestDetails() {
  const {params} = useRouteMatch<{id: string}>();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const order_type = queryParams.get('order_type') as OrderType | undefined;

  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<SelectOption>();
  const [sendChannels, setSendChannels] = useState({
    sms: true,
    email: false,
    push: false,
  });
  const [copayItems, setCopayItems] = useState<any[]>([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [timelineSort, setTimelineSort] = useState<NoteSort | 'all'>('all');
  const [manualChargeType, setManualChargeType] = useState<
    'receivable' | 'payable'
  >('receivable');
  const form = useForm<ManualCharge>();

  const [selectedSplitItems, setSelectedSplitItems] = useState<number[]>([]);
  const [isSplitting, setIsSplitting] = useState(false);
  const {isPropertyManager, hasFeature, labels} = useCompanyConfig();
  const queryCach = queryClient.getQueryCache().getAll();

  const [, , order, status] =
    (findLast(queryCach, (q) => q.queryKey[0] === 'requests')
      ?.queryKey as string[]) || [];

  const {data: messageTemplates} = useQuery(
    ['message_templates'],
    getMessageTemplates,
    {
      refetchOnWindowFocus: false,
    }
  );
  const {data, refetch} = useQuery(
    ['request_details', params.id, order, status, order_type],
    getRequestById
  );
  const {data: landlord} = useQuery(
    ['landlord', data?.company_property?.landlord],
    getUserById,
    {enabled: hasFeature('hasPropertyDetailsOnOrderDetails')}
  );

  const updateScriptStatus = useMutation(
    'update_script_status',
    async ({
      drugId,
      status,
      originalId,
    }: {
      drugId: number;
      status: ScriptStatus | MMScriptStatus;
      originalId: string | number;
    }) => {
      const {data} = await api.post<{status: ScriptStatus}>(
        `/requests/${originalId}/prescriptions/${drugId}/status/`,
        {status}
      );
      return {...data, drugId};
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(['request_details']);
      },
    }
  );

  const updatePreferenceMutation = useMutation({
    mutationFn: updatePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries(['request_details', params.id]);
    },
  });
  // const updatePreference = useMutation(
  //   'update_preference',
  //   async (datas: {
  //     preference_date?: string | null;
  //     preference_time?: string | null;
  //   }) => {
  //     const {data: res} = await api.patch<{
  //       preference_date: string;
  //       preference_time: string;
  //     }>(`/requests/${data?.id}/`, datas);
  //     return {...res};
  //   },
  //   {
  //     onSettled: () => {
  //       queryClient.invalidateQueries(['request_details', params?.id]);
  //     },
  //   }
  // );

  const createNote = useMutation(
    'create_note',
    async ({
      text,
      driver,
    }: {
      text: string;
      driver: boolean;
    }): Promise<string> => {
      const {data: res} = await api.post<{
        text: string;
        is_visible_to_drivers: boolean;
      }>(`/requests/${data?.id}/note/`, {
        text,
        is_visible_to_drivers: driver,
      });
      return res.text;
    },
    {
      onMutate: async ({text, driver}) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(['request_details', params.id]);

        // Snapshot the previous value
        const previousRequest = queryClient.getQueryData<RequestDetails>([
          'request_details',
          params.id,
        ]);

        const newNote: Note = {
          id: Date.now(),
          text,
          is_visible_to_drivers: driver,
          author: getFullName(currentUser),
          avatar: currentUser.avatar,
          created_at: formatISO(Date.now()),
        };

        const nextRequest: RequestDetails | undefined = previousRequest
          ? {
              ...previousRequest,
              notes: [newNote, ...previousRequest.notes],
            }
          : undefined;

        // Optimistically update to the new value
        queryClient.setQueryData(['request_details', params.id], nextRequest);

        // Return a context object with the snapshotted value
        return {previousRequest};
      },

      // If the mutation fails, use the context returned from onMutate to roll back
      onError: (err, _text, context?: {previousRequest?: RequestDetails}) => {
        queryClient.setQueryData(
          ['request_details', params.id],
          context?.previousRequest
        );
      },

      // Always refetch after error or success:
      onSettled: () => {
        queryClient.invalidateQueries(['request_details', params.id]);
      },
    }
  );

  useEffect(() => {
    if (data?.prescriptions) {
      if (manualChargeType === 'receivable') {
        const receivableCopayItems: CopayItem[] =
          data?.prescriptions?.reduce((pr, curr) => {
            if (Number(curr.amount_due) > 0) {
              return [
                ...pr,
                {id: curr.id, amount_due: Number(curr.amount_due) || 0},
              ];
            }

            return pr;
          }, [] as CopayItem[]) || [];
        setCopayItems(receivableCopayItems);
      }

      if (manualChargeType === 'payable') {
        const payableCopayItems: CopayItem[] =
          data?.prescriptions?.reduce((pr, curr) => {
            if (Number(curr.payable_amount_due) > 0) {
              return [
                ...pr,
                {id: curr.id, amount_due: Number(curr.payable_amount_due) || 0},
              ];
            }

            return pr;
          }, [] as CopayItem[]) || [];
        setCopayItems(payableCopayItems);
      }
      setAllSelected(true);
    }
  }, [data?.prescriptions, manualChargeType]);

  // function onChangeTime(value: {
  //   preference_date?: string | null;
  //   preference_time?: string | null;
  // }) {
  //   if (!data) return;

  //   updatePreference.mutate({
  //     ...value,
  //   });
  // }

  function onChangeScriptStatus(
    newValue: SingleValue<ScriptStatusOption>,
    actionMeta: ActionMeta<SelectOption>,
    originalId: string | number
  ) {
    const drugId = Number(actionMeta.name?.split('-')[1]);
    if (newValue) {
      const status = newValue.value;
      updateScriptStatus.mutate({drugId, status, originalId});
    }
  }
  const onChangePreference = async (params: UpdatePreferencesParams) => {
    await updatePreferenceMutation.mutateAsync(params);
  };
  // function onChangePriority(newValue: string | number | undefined) {
  //   if (data) {
  //     api
  //       .patch(`/requests/${data.id}/`, {
  //         priority: newValue || null,
  //       })
  //       .then(() => {
  //         refetch();
  //       })
  //       .catch(() => {});
  //   }
  // }

  function onSubmitNote(text: string, driver: boolean) {
    return createNote.mutate({text, driver});
  }

  async function chargeCard(datas: any) {
    const data = await api.post<any>(`/payment/charge/`, datas);
    refetch();
    return data;
  }
  async function chargeManually() {
    const body: any = {
      customer: data?.customer.id,
      copay_items: copayItems,
      payment_method: form.getValues('payment_method'),
      transaction_type: manualChargeType,
    };
    if (isPropertyManager) {
      body.payer_type = form.getValues('payer_type');
      body.payer_name = form.getValues('payer_name');
    }
    const res = await api.post<any>(`/payment/charge-manual/`, body);
    refetch();
    return res;
  }
  const sendMessage = async () => {
    if (!data?.id) {
      return;
    }
    if (message.trim() === '') return;
    try {
      await api.post(`/requests/${data.id}/message-customer/`, {
        message: message,
        is_sms: sendChannels.sms,
        is_email: sendChannels.email,
        is_app_push: sendChannels.push,
        // customer_ids: [data?.customer.id],
      });
      toast.success('Message sent successfully');
      setShowMessageModal(false);
      setMessage('');
      refetch();
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const splitOrderItems = async () => {
    if (!data?.id) return;
    if (selectedSplitItems.length === 0) return;
    setIsSplitting(true);
    try {
      await api.post(`/requests/${data.id}/split/`, {
        request_id: data.id,
        request_drug_ids: selectedSplitItems,
      });
      toast.success('Order split successfully');
      setSelectedSplitItems([]);
      refetch();
    } catch (error) {
      const message = get(
        error,
        'response.data.detail',
        'Failed to split order'
      );
      toast.error(message);
    } finally {
      setIsSplitting(false);
    }
  };

  const orderItems = useMemo(() => {
    return data?.prescriptions?.map((item) => ({
      checkbox: (
        <div
          onClick={(val) => {
            val.stopPropagation();
            const include = copayItems.find((i) => i.id === item.id);

            if (include) {
              setCopayItems(copayItems.filter((i) => i.id !== item.id));
            } else {
              setCopayItems([
                ...copayItems,
                {
                  id: item.id,
                  amount_due:
                    manualChargeType === 'payable'
                      ? item.payable_amount_due
                      : item.amount_due,
                },
              ]);
            }
          }}
        >
          <CheckIcon
            emptySelect={!copayItems.find((i) => i.id === item.id)}
            allSelected={copayItems.find((i) => i.id === item.id)}
          />
        </div>
      ),
      id: item.id,
      name: item.name,
      quantity: item.quantity,
      cash_price: item.cash_price,
      amount_due: item.amount_due,
      payable_amount_due: item.payable_amount_due,
      payable_total_amount: item.payable_total_amount,
      profit: Number(
        parseFloat(item.total_amount.toString()) -
          parseFloat(item.payable_total_amount?.toString() || '0')
      ),
      type:
        item?.offering_type &&
        OFFERING_TYPE_OPTIONS_LABELS[item?.offering_type as OfferingType],
    }));
  }, [data?.prescriptions, copayItems, manualChargeType]);

  const notes = useMemo(() => {
    return (
      data?.notes?.filter((item) => {
        if (timelineSort === 'all') {
          return true;
        }
        return item.note_type === timelineSort;
      }) || []
    );
  }, [data?.notes, timelineSort]);

  const onClickTrackingMessage = () => {
    if (!data?.id) {
      return;
    }
    setShowMessageModal(true);
    const message = `Hi, this is Magic Dispatch. You can track your Order #${data?.sequential_order_number} using the following link: ${process.env.REACT_APP_TRACKING_URL}?id=${data?.tracking_id} \n\nThanks!`;
    setMessage(message);
  };

  return {
    refetch,
    data,
    landlord,
    onSubmitNote,
    onChangeScriptStatus,
    chargeCard,
    chargeManually,
    onChangePreference,
    message,
    setMessage,
    showMessageModal,
    setShowMessageModal,
    sendChannels,
    setSendChannels,
    selectedTemplate,
    setSelectedTemplate,
    sendMessage,
    messageTemplates: messageTemplates?.map((temp) => ({
      label: temp.title,
      value: temp?.content,
    })),
    orderItems,
    copayItems,
    setCopayItems,
    allSelected,
    setAllSelected,
    notes,
    timelineSort,
    setTimelineSort,
    form,
    selectedSplitItems,
    setSelectedSplitItems,
    splitOrderItems,
    isSplitting,
    isPropertyManager,
    manualChargeType,
    setManualChargeType,
    labels,
    onClickTrackingMessage,
  };
}

export default useRequestDetails;
